import { addToCart } from '../cart/cartStore.ts';
import { getActiveColorId } from '../cart/cartActions.ts';

/** Sélection de couleur propre à la fiche produit — clic uniquement (contrairement
 * à `initColorSwitch` de `client/productCard.ts`, partagé avec les cartes
 * boutique/home, qui prévisualise au survol). Ici la couleur active détermine
 * quelle pile d'images (`.produit__gallery-stack`) est affichée, et un aperçu au
 * survol pendant que la page est scrollée loin dans une pile provoquait un
 * "freeze" du scroll : la hauteur de page s'effondre d'un coup si la nouvelle
 * couleur a moins d'images, avant même que l'utilisateur ait cliqué. On remonte
 * donc systématiquement en haut de page au clic pour repartir d'une position de
 * scroll toujours cohérente avec la pile affichée. */
function initProduitColorSwitch(): void {
  const root = document.querySelector<HTMLElement>('.produit[data-color-root]');
  if (!root) return;

  const swatches = Array.from(root.querySelectorAll<HTMLElement>('.color-swatch'));
  if (swatches.length < 2) return;

  const panels = root.querySelectorAll<HTMLElement>('.color-panel');

  function apply(colorId: string): void {
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.colorId !== colorId;
    });
    swatches.forEach((s) => s.classList.toggle('is-active', s.dataset.colorId === colorId));
  }

  swatches.forEach((swatch) => {
    swatch.addEventListener('click', () => {
      apply(swatch.dataset.colorId ?? '');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  });
}

/** Onglets EU/US au-dessus des pastilles de taille — bascule le libellé affiché
 * sur toutes les pastilles (toutes couleurs confondues, pour rester cohérent si
 * l'utilisateur change de couleur après avoir basculé l'unité). La sélection
 * reste toujours indexée par EU (data-size-eu), inchangée par ce toggle. */
function initUnitToggle(): void {
  const toggle = document.querySelector<HTMLElement>('.produit__unit-toggle');
  if (!toggle) return;
  const buttons = Array.from(toggle.querySelectorAll<HTMLButtonElement>('.produit__unit-btn'));
  if (buttons.length === 0) return;

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const unit = btn.dataset.unit === 'us' ? 'us' : 'eu';
      buttons.forEach((b) => b.classList.toggle('is-active', b === btn));
      document.querySelectorAll<HTMLButtonElement>('.produit__size-pill').forEach((pill) => {
        const value = unit === 'us' ? pill.dataset.sizeUs : pill.dataset.sizeEu;
        pill.textContent = `${unit.toUpperCase()} ${value ?? ''}`;
      });
    });
  });
}

/** Sidebar "Product details" / "Delivery & Returns" — même schéma que
 * `client/cart/cartSidebar.ts` (is-open, aria-hidden, Escape, backdrop),
 * propre à cette page (pas de réutilisation de la sidebar panier). */
function initInfoSidebar(): void {
  const sidebar = document.getElementById('produit-info-sidebar');
  const backdrop = document.getElementById('produit-info-backdrop');
  const closeBtn = document.getElementById('produit-info-close');
  const triggers = document.querySelectorAll<HTMLButtonElement>('.produit__detail-trigger');
  const panels = document.querySelectorAll<HTMLElement>('.produit-info-panel');
  if (!sidebar || !backdrop || !closeBtn || triggers.length === 0) return;

  function open(panelName: string): void {
    panels.forEach((p) => {
      p.hidden = p.dataset.infoPanel !== panelName;
    });
    sidebar!.classList.add('is-open');
    backdrop!.classList.add('is-open');
    sidebar!.setAttribute('aria-hidden', 'false');
  }

  function close(): void {
    sidebar!.classList.remove('is-open');
    backdrop!.classList.remove('is-open');
    sidebar!.setAttribute('aria-hidden', 'true');
  }

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const panelName = trigger.dataset.infoPanel;
      if (panelName) open(panelName);
    });
  });

  closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && sidebar!.classList.contains('is-open')) close();
  });
}

/** Rend cliquables les pastilles de taille (`.produit__size-pill`) — sélection
 * automatique de la première taille disponible de la couleur active, câble le
 * bouton "Ajouter au panier" (quantité fixée à 1 — l'ajustement se fait dans le
 * panier, plus de stepper sur la fiche produit). */
function initProduitAddToCart(): void {
  const root = document.querySelector<HTMLElement>('.produit[data-color-root]');
  if (!root) return;

  const productId = root.dataset.productId;
  const addBtn = root.querySelector<HTMLButtonElement>('.produit__add-to-cart-btn');
  if (!productId || !addBtn) return;

  function selectedPill(): HTMLElement | null {
    const colorId = getActiveColorId(root!);
    if (!colorId) return null;
    const wrap = root!.querySelector<HTMLElement>(`.produit__size-pills-wrap[data-color-id="${CSS.escape(colorId)}"]`);
    return wrap?.querySelector<HTMLElement>('.produit__size-pill.is-selected') ?? null;
  }

  document.querySelectorAll<HTMLElement>('.produit__size-pills').forEach((panel) => {
    const pills = Array.from(panel.querySelectorAll<HTMLButtonElement>('.produit__size-pill:not([disabled])'));
    if (pills.length === 0) return;

    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        pills.forEach((p) => p.classList.toggle('is-selected', p === pill));
      });
    });

    pills[0]!.classList.add('is-selected');
  });

  addBtn.addEventListener('click', () => {
    const colorId = getActiveColorId(root);
    const pill = selectedPill();
    if (!colorId || !pill) return;

    const sizeEu = pill.dataset.sizeEu;
    const stock = Number(pill.dataset.stock ?? '0');
    if (!sizeEu || stock <= 0) return;

    addToCart({ productId, colorId, sizeEu, quantity: 1 }, stock);
  });
}

/** Carrousel des pastilles de taille (`.produit__size-pills-wrap`) — mêmes
 * mécanique/paramètres que `initSizesScroll` de `client/productCard.ts`
 * (cartes boutique/home), réimplémentée ici avec les classes propres à la
 * fiche produit plutôt que de réutiliser les classes `.product-card__*`. */
function initSizesCarousel(): void {
  document.querySelectorAll<HTMLElement>('.produit__size-pills-wrap').forEach((wrap) => {
    const list = wrap.querySelector<HTMLElement>('.produit__size-pills');
    const prev = wrap.querySelector<HTMLButtonElement>('.produit__sizes-scroll-btn--prev');
    const next = wrap.querySelector<HTMLButtonElement>('.produit__sizes-scroll-btn--next');
    if (!list || !prev || !next) return;

    const STEP = 80;

    function sync(): void {
      const overflow = list!.scrollWidth > list!.clientWidth + 1;
      prev!.classList.toggle('is-visible', overflow && list!.scrollLeft > 0);
      next!.classList.toggle('is-visible', overflow && list!.scrollLeft < list!.scrollWidth - list!.clientWidth - 1);
    }

    prev.addEventListener('click', () => { list.scrollBy({ left: -STEP, behavior: 'smooth' }); });
    next.addEventListener('click', () => { list.scrollBy({ left: STEP, behavior: 'smooth' }); });
    list.addEventListener('scroll', sync, { passive: true });
    sync();
    setTimeout(sync, 150);
  });
}

initProduitColorSwitch();
initUnitToggle();
initInfoSidebar();
initProduitAddToCart();
initSizesCarousel();
